import { LABEL_CLASS } from '@/components/common/node-view-settings-panel';
import { CommonNodeViewWrapper } from '@/components/common/node-view-wrapper';
import { AlignBottomIcon } from '@/components/tiptap-editor/tiptap-icons/align-bottom-icon';
import { AlignTopIcon } from '@/components/tiptap-editor/tiptap-icons/align-top-icon';
import { Button } from '@/components/tiptap-editor/tiptap-ui-primitive/button';
import { Button as UiButton } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Input } from '@/components/ui/input';
import { useIsEditorReadonly } from '@/hooks/use-is-editor-readonly';
import { moveNode } from '@/hooks/use-move-node';
import type { Editor } from '@tiptap/core';
import type { Node as PMNode } from '@tiptap/pm/model';
import type { Transaction } from '@tiptap/pm/state';
import type { ReactNodeViewProps } from '@tiptap/react';
import { NodeViewContent } from '@tiptap/react';
import { ChevronDown, Plus, Trash2 } from 'lucide-react';
import { memo, useEffect, useState } from 'react';
import {
    ITLAB_SECTION,
    ITLAB_SECTION_ATTR,
    countChildType,
    getChildText,
    getZyITLabTaskIndexInRegion,
    getZyITLabTaskRegion,
    sumGoalPoints,
} from './zyitlab-shared';

type TaskContext = {
    region: 'optional' | 'tasks';
    nodeIndex: number;
    totalNodes: number;
    sectionAttr: (typeof ITLAB_SECTION)[keyof typeof ITLAB_SECTION];
    labelPrefix: string;
};

const FALLBACK_TASK_CONTEXT: TaskContext = {
    region: 'tasks',
    nodeIndex: -1,
    totalNodes: 0,
    sectionAttr: ITLAB_SECTION.TASKS,
    labelPrefix: 'Task',
};

function getTaskContext(editor: Editor, pos: number | undefined): TaskContext {
    if (typeof pos !== 'number') {
        return FALLBACK_TASK_CONTEXT;
    }

    const $pos = editor.state.doc.resolve(pos);
    const parent = $pos.parent;
    const taskChildIndex = $pos.index();
    const region = getZyITLabTaskRegion(parent, taskChildIndex);
    const { nodeIndex, totalNodes } = getZyITLabTaskIndexInRegion(parent, taskChildIndex, region);

    return {
        region,
        nodeIndex,
        totalNodes,
        sectionAttr: region === 'optional' ? ITLAB_SECTION.OPTIONAL_TASKS : ITLAB_SECTION.TASKS,
        labelPrefix: region === 'optional' ? 'Optional task' : 'Task',
    };
}

function taskContextEqual(a: TaskContext, b: TaskContext): boolean {
    return (
        a.region === b.region &&
        a.nodeIndex === b.nodeIndex &&
        a.totalNodes === b.totalNodes &&
        a.sectionAttr === b.sectionAttr &&
        a.labelPrefix === b.labelPrefix
    );
}

function useZyITLabTaskContext(editor: Editor, getPos: () => number | undefined): TaskContext {
    const [taskContext, setTaskContext] = useState(() => getTaskContext(editor, getPos()));

    useEffect(() => {
        const handleUpdate = ({ transaction }: { transaction: Transaction }) => {
            if (!transaction.docChanged) {
                return;
            }
            setTaskContext(prev => {
                const next = getTaskContext(editor, getPos());
                return taskContextEqual(prev, next) ? prev : next;
            });
        };

        editor.on('transaction', handleUpdate);
        return () => {
            editor.off('transaction', handleUpdate);
        };
    }, [editor, getPos]);

    return taskContext;
}

function canMoveTaskAmongRegionTasks(parent: PMNode, taskChildIndex: number, direction: 'up' | 'down'): boolean {
    const neighborIndex = direction === 'up' ? taskChildIndex - 1 : taskChildIndex + 1;
    if (neighborIndex < 0 || neighborIndex >= parent.childCount) {
        return false;
    }
    const neighbor = parent.child(neighborIndex);
    if (neighbor.type.name !== 'zyITLabTask') {
        return false;
    }
    return getZyITLabTaskRegion(parent, taskChildIndex) === getZyITLabTaskRegion(parent, neighborIndex);
}

export const ZyITLabTaskNodeView: React.FC<ReactNodeViewProps> = memo(
    ({ node, updateAttributes, selected, editor, getPos, deleteNode, decorations }) => {
        const isEditorReadonly = useIsEditorReadonly(editor);
        const [open, setOpen] = useState(false);
        const pos = getPos();
        const taskContext = useZyITLabTaskContext(editor, getPos);

        const stepCount = countChildType(node, 'zyITLabStep');
        const totalPoints = (() => {
            let points = 0;
            node.forEach(child => {
                if (child.type.name === 'zyITLabStep') {
                    points += sumGoalPoints(child);
                }
            });
            return points;
        })();

        const handleMoveUp = () => {
            if (typeof pos !== 'number') return;
            const $pos = editor.state.doc.resolve(pos);
            if (!canMoveTaskAmongRegionTasks($pos.parent, $pos.index(), 'up')) {
                return;
            }
            moveNode(editor, 'up', { pos, node });
        };

        const handleMoveDown = () => {
            if (typeof pos !== 'number') return;
            const $pos = editor.state.doc.resolve(pos);
            if (!canMoveTaskAmongRegionTasks($pos.parent, $pos.index(), 'down')) {
                return;
            }
            moveNode(editor, 'down', { pos, node });
        };

        const handleDelete = () => {
            if (taskContext.region === 'tasks' && taskContext.totalNodes <= 1) {
                return;
            }
            if (window.confirm('Delete this task?')) {
                deleteNode();
            }
        };

        const summaryLabel =
            node.attrs.taskDescription?.trim() ||
            getChildText(node, 'zyITLabTaskInstructions').trim() ||
            `${taskContext.labelPrefix} ${taskContext.nodeIndex + 1}`;

        const canMoveUp =
            taskContext.nodeIndex > 0 &&
            typeof pos === 'number' &&
            canMoveTaskAmongRegionTasks(editor.state.doc.resolve(pos).parent, editor.state.doc.resolve(pos).index(), 'up');
        const canMoveDown =
            taskContext.nodeIndex >= 0 &&
            taskContext.nodeIndex < taskContext.totalNodes - 1 &&
            typeof pos === 'number' &&
            canMoveTaskAmongRegionTasks(editor.state.doc.resolve(pos).parent, editor.state.doc.resolve(pos).index(), 'down');
        const canDelete = taskContext.region === 'optional' || taskContext.totalNodes > 1;

        return (
            <CommonNodeViewWrapper
                decorations={decorations}
                label={`${taskContext.labelPrefix} ${taskContext.nodeIndex + 1}`}
                selected={selected}
                containerClassName="mb-3 rounded-lg border border-gray-300 bg-white dark:border-gray-600 dark:bg-gray-950"
                contentClassName="px-4"
                containerProps={{ [ITLAB_SECTION_ATTR]: taskContext.sectionAttr }}
            >
                <Collapsible
                    open={open}
                    onOpenChange={setOpen}
                >
                    <div
                        className="flex items-center gap-2 border-gray-200 px-4 py-3 dark:border-gray-700"
                        contentEditable={false}
                    >
                        <CollapsibleTrigger className="flex flex-1 items-center gap-2 text-left">
                            <ChevronDown className={`h-4 w-4 transition-transform ${open ? '' : '-rotate-90'}`} />
                            <div className="flex justify-between items-center gap-6 w-full">
                                <div className="text-sm font-semibold text-gray-900 dark:text-gray-100 w-56 truncate">{summaryLabel}</div>
                                <div className="text-sm text-gray-500">
                                    {stepCount} step{stepCount === 1 ? '' : 's'} · {totalPoints} point{totalPoints === 1 ? '' : 's'}
                                </div>
                            </div>
                        </CollapsibleTrigger>
                        <div className="flex items-center gap-1">
                            <Button
                                onClick={handleMoveUp}
                                data-style="ghost"
                                disabled={!canMoveUp || isEditorReadonly}
                                aria-label="Move task up"
                            >
                                <AlignTopIcon className="h-4 w-4" />
                            </Button>
                            <Button
                                onClick={handleMoveDown}
                                data-style="ghost"
                                disabled={!canMoveDown || isEditorReadonly}
                                aria-label="Move task down"
                            >
                                <AlignBottomIcon className="h-4 w-4" />
                            </Button>
                            <Button
                                onClick={handleDelete}
                                data-style="ghost"
                                disabled={!canDelete || isEditorReadonly}
                                aria-label="Delete task"
                            >
                                <Trash2 className="h-4 w-4" />
                            </Button>
                        </div>
                    </div>
                    <CollapsibleContent>
                        <div
                            className="mb-2"
                            contentEditable={false}
                        >
                            <label
                                className={LABEL_CLASS}
                                contentEditable={false}
                            >
                                Task description
                            </label>
                            <Input
                                className="mt-1"
                                value={node.attrs.taskDescription || ''}
                                onChange={e => updateAttributes({ taskDescription: e.target.value })}
                                readOnly={isEditorReadonly}
                                contentEditable={false}
                            />
                        </div>
                        <NodeViewContent />
                        <div
                            className="mb-3 flex justify-end gap-2"
                            contentEditable={false}
                        >
                            <UiButton
                                type="button"
                                variant="outline"
                                size="sm"
                                disabled={isEditorReadonly}
                                onClick={() => editor.commands.addZyITLabStep(getPos())}
                            >
                                <Plus className="mr-2 h-4 w-4" />
                                Add step
                            </UiButton>
                        </div>
                    </CollapsibleContent>
                </Collapsible>
            </CommonNodeViewWrapper>
        );
    },
);

ZyITLabTaskNodeView.displayName = 'ZyITLabTaskNodeView';
